import { Injectable, inject } from '@angular/core';
import { Observable, from, map, catchError, throwError } from 'rxjs';
import {
    createActorWebClient,
    ServiceClientError,
    type ActorWebClient,
} from '@workgraph/actor-service-web-client';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import type { Agent, CreateAgentRequest, UpdateAgentRequest } from '../models/agent.model';

/** How the caught error was classified — components branch on this, never on message text. */
export type AgentErrorKind = 'runtime-unavailable' | 'not-found' | 'generic';

/**
 * A renderable, typed error the components can branch on by CODE rather than message text.
 *
 * `.message` is always the user-facing copy (components render `err.message`); `.kind` and
 * `.status` are the reliable discriminators derived from the client's `statusCode`.
 */
export class AgentError extends Error {
    constructor(
        message: string,
        readonly kind: AgentErrorKind,
        readonly status?: number,
    ) {
        super(message);
        this.name = 'AgentError';
    }
}

type AgentOperation = 'create' | 'update' | 'read';

/**
 * Thin Angular seam between DI and the generated actor-service web-client.
 *
 * It owns exactly four concerns and NOTHING else (no per-endpoint URL/HTTP wiring — that
 * lives in the generated SDK): (1) constructing the web-client with the api-gateway host and
 * the Cognito bearer token, (2) bridging the client's promises to RxJS Observables,
 * (3) unwrapping the `{data}` / `{data,count}` response envelopes, and (4) mapping errors to
 * friendly, renderable, TYPED `AgentError`s.
 *
 * Error mapping note: the web-client's response interceptor THROWS a `ServiceClientError`
 * (carrying a reliable `.statusCode` and a `.message` derived from the HTTP `statusText`) on
 * any status >= 400 — the throw pre-empts the hey-api `{ error }` result, so the adapter does
 * NOT see the backend's JSON `{error}` body. `.statusCode` is ALWAYS present; `.message` is
 * NOT reliable (it is `statusText`, which is a bare "Bad Request"/"Not Found" on 1.0.0 and
 * EMPTY over HTTP/2 in prod). Detection therefore derives PURELY from `statusCode`:
 * create/update 400 → runtime-unavailable (the runtime is the only server-validated
 * selectable in this UI); any 404 → not-found; everything else → a per-operation fallback.
 * A meaningful, non-generic `.message` (present only once 1.0.1+ reads the `{error}` body) is
 * surfaced as a fidelity bonus but is never depended upon.
 */
@Injectable({ providedIn: 'root' })
export class AgentGateway {
    private readonly auth = inject(AuthService);

    private readonly client: ActorWebClient = createActorWebClient({
        baseUrl: environment.gatewayBaseUrl,
        getToken: () => this.auth.getIdToken(),
    });

    private static readonly RUNTIME_UNAVAILABLE_COPY =
        'That runtime is currently unavailable. Try a different compute profile.';

    listAgents(projectId: string): Observable<Agent[]> {
        return from(this.client.listAgents({ path: { projectId } })).pipe(
            map((result) => result.data?.data ?? []),
            catchError((err) =>
                this.fail(err, 'read', 'We could not load the agents. Please try again.'),
            ),
        );
    }

    getAgent(projectId: string, id: string): Observable<Agent> {
        return from(this.client.getAgent({ path: { projectId, id } })).pipe(
            map((result) => this.requireData(result.data?.data, 'agent')),
            catchError((err) =>
                this.fail(err, 'read', 'We could not load this agent. Please try again.'),
            ),
        );
    }

    createAgent(projectId: string, body: CreateAgentRequest): Observable<Agent> {
        return from(this.client.createAgent({ path: { projectId }, body })).pipe(
            map((result) => this.requireData(result.data?.data, 'agent')),
            catchError((err) =>
                this.fail(err, 'create', 'We could not create the agent. Please try again.'),
            ),
        );
    }

    updateAgent(projectId: string, id: string, body: UpdateAgentRequest): Observable<Agent> {
        return from(this.client.updateAgent({ path: { projectId, id }, body })).pipe(
            map((result) => this.requireData(result.data?.data, 'agent')),
            catchError((err) =>
                this.fail(err, 'update', 'We could not update the agent. Please try again.'),
            ),
        );
    }

    deleteAgent(projectId: string, id: string): Observable<void> {
        return from(this.client.deleteAgent({ path: { projectId, id } })).pipe(
            map(() => void 0),
            catchError((err) =>
                this.fail(err, 'read', 'We could not delete the agent. Please try again.'),
            ),
        );
    }

    /** Guard against a 2xx with an unexpectedly empty envelope. */
    private requireData<T>(data: T | undefined, what: string): T {
        if (data === undefined || data === null) {
            throw new Error(`The server returned no ${what} data.`);
        }
        return data;
    }

    /** Map a thrown client error to a friendly, renderable, TYPED `AgentError`. */
    private fail(err: unknown, op: AgentOperation, fallback: string): Observable<never> {
        return throwError(() => this.toAgentError(err, op, fallback));
    }

    /**
     * Classify the caught error by `statusCode` (reliable) — never by message text (unreliable).
     * - create/update 400 → runtime-unavailable friendly copy (unless a specific, non-generic
     *   `{error}` message is present from 1.0.1+, which we surface instead as a bonus).
     * - any 404 → not-found.
     * - everything else → the per-operation fallback (or a specific non-generic message if present).
     */
    private toAgentError(err: unknown, op: AgentOperation, fallback: string): AgentError {
        if (err instanceof ServiceClientError) {
            const status = err.statusCode;
            const specific = this.meaningfulMessage(err.message);

            if (status === 404) {
                return new AgentError(specific ?? 'This agent was not found.', 'not-found', 404);
            }
            if (status === 400 && (op === 'create' || op === 'update')) {
                return new AgentError(
                    specific ?? AgentGateway.RUNTIME_UNAVAILABLE_COPY,
                    'runtime-unavailable',
                    400,
                );
            }
            return new AgentError(specific ?? fallback, 'generic', status);
        }
        if (err instanceof Error && err.message) {
            return new AgentError(err.message, 'generic');
        }
        return new AgentError(fallback, 'generic');
    }

    /**
     * Return the error's own message only when it carries something meaningful — i.e. non-empty
     * AND not a bare HTTP reason phrase. Returns `undefined` otherwise so callers fall back.
     */
    private meaningfulMessage(raw: string | undefined | null): string | undefined {
        const message = (raw ?? '').trim();
        if (!message || this.isGenericHttpMessage(message)) {
            return undefined;
        }
        return message;
    }

    private isGenericHttpMessage(message: string): boolean {
        const generic = [
            'bad request',
            'not found',
            'internal server error',
            'forbidden',
            'unauthorized',
        ];
        const normalized = message.toLowerCase();
        return generic.some(
            (g) => normalized === g || normalized === `request failed with status ${g}`,
        );
    }
}
